import React, { useState, useRef, useEffect } from "react";
import { apiClient } from "../services/api";
import type { ModelInfo } from "../types/api";
import { useUser } from "../hooks/useApi";

interface ModelSelectorProps {
  selectedModel: string;
  onModelChange: (model: string) => void;
  onModelCreditsChange?: (modelCredits: Record<string, number>) => void;
  type?: "t2v" | "i2v" | "t2i" | "i2i";
  disabled?: boolean;
  defaultModelInfo?: ModelInfo;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({
  selectedModel,
  onModelChange,
  onModelCreditsChange,
  type = "t2v",
  disabled = false,
  defaultModelInfo,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user: userData } = useUser();

  // Check if user is on free plan
  const isFreePlan = userData?.subscription.plan === "free";

  // Icon mapping for models
  const getModelIcon = (modelName: string) => {
    const name = modelName.toLowerCase();
    if (name.includes("veo")) return "🎬";
    if (name.includes("minimax")) return "🌟";
    if (name.includes("wan")) return "⚡";
    return "🤖";
  };

  const selectedModelData =
    models.find((m) => m.name === selectedModel) ||
    (defaultModelInfo && defaultModelInfo.name === selectedModel
      ? defaultModelInfo
      : undefined);

  // Fetch models from API
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log("🔍 ModelSelector: Fetching models for type:", type);
        console.log("🔍 User agent:", navigator.userAgent);
        const response = await apiClient.getModels({ type: type as "t2v" | "i2v" | "t2i" | "i2i" });
        console.log("✅ ModelSelector: Models fetched successfully:", response.models.length);
        setModels(response.models);

        // Create model credits map and pass to parent
        const creditsMap = response.models.reduce((acc, model) => {
          acc[model.name] = model.credits;
          return acc;
        }, {} as Record<string, number>);
        onModelCreditsChange?.(creditsMap);

        // If no model is selected or the selected model is not in the current list, select the first model (Veo3)
        const isValidModel = response.models.some(
          (model) => model.name === selectedModel
        );
        if ((!selectedModel || !isValidModel) && response.models.length > 0) {
          onModelChange(response.models[0].name);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to load models";
        console.error("❌ ModelSelector: Failed to fetch models:", err);
        console.error("Error details:", {
          message: errorMessage,
          stack: err instanceof Error ? err.stack : undefined,
          type,
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
        });
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchModels().catch((err) => {
      // Double catch to ensure errors don't bubble up to error boundary
      console.error("❌ ModelSelector: Unhandled error in fetchModels:", err);
      setError("Failed to load models");
      setLoading(false);
    });
    // Only refetch when type or userData changes, not when callbacks are recreated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, userData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="relative mb-6 max-w-md">
        <label className="block text-sm font-medium text-white dark:text-white mb-2">
          Model
        </label>
        <div className="w-full bg-white/5 dark:bg-white/5 border border-white/20 rounded-xl p-4 flex items-center justify-center transition-colors">
          <div className="text-gray-400 dark:text-gray-400">Loading models...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative mb-6 max-w-md">
        <label className="block text-sm font-medium text-white dark:text-white mb-2">
          Model
        </label>
        <div className="w-full bg-red-900/20 dark:bg-red-900/20 border border-red-500/50 dark:border-red-500/50 rounded-xl p-4 flex items-center justify-center transition-colors">
          <div className="text-red-400 dark:text-red-400 text-sm">
            Error: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative mb-6 w-full max-w-md md:max-w-lg"
      ref={dropdownRef}
    >
      <label className="block text-sm font-medium text-white dark:text-white mb-2">
        Model
      </label>

      {/* Dropdown Trigger */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full bg-white/5 dark:bg-white/5 border-2 border-white/20 text-white rounded-xl p-3 flex items-center justify-between transition-all duration-200 ${
          disabled
            ? "opacity-70 cursor-not-allowed"
            : "hover:bg-white/10 hover:border-white/40 dark:hover:bg-white/10 dark:hover:border-white/40 cursor-pointer hover:shadow-[0_0_15px_rgba(255,255,255,0.1)]"
        }`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 bg-white/10 dark:bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
            {selectedModelData?.logo_url ? (
              <img
                src={selectedModelData.logo_url}
                alt={selectedModelData.public_name}
                className="w-6 h-6 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  (e.currentTarget
                    .nextElementSibling as HTMLElement)!.style.display =
                    "block";
                }}
              />
            ) : null}
            <span
              className="text-2xl"
              style={{
                display: selectedModelData?.logo_url ? "none" : "block",
              }}
            >
              🤖
            </span>
          </div>
          <div className="text-left flex-1 min-w-0">
            <div className="font-semibold text-base flex items-center gap-2 flex-wrap">
              <span className="truncate text-white dark:text-white">
                {selectedModelData?.public_name || "Select a model"}
              </span>
              {selectedModel === "wan2.5" && (
                <>
                  <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                    AUDIO
                  </span>
                  <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                    NEW
                  </span>
                </>
              )}
              {!isFreePlan && (
                <span className="bg-white/20 dark:bg-white/20 text-white text-xs px-2.5 py-1 rounded-full font-semibold">
                  {selectedModelData?.credits || 0} credits
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center ml-2">
          <svg
            className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-black dark:bg-black border-2 border-white/30 dark:border-white/30 rounded-xl shadow-[0_0_30px_rgba(255,255,255,0.2)] z-50 max-h-96 overflow-y-auto transition-colors">
          {models.map((model, index) => {
            const isSelected = selectedModel === model.name;
            const isWan25 = model.name === "wan2.5";

            return (
              <div
                key={model.name}
                onClick={() => {
                  onModelChange(model.name);
                  setIsOpen(false);
                }}
                className={`flex items-start gap-3 p-4 cursor-pointer transition-all duration-200 ${
                  isSelected
                    ? "bg-white/10 dark:bg-white/10"
                    : "hover:bg-white/5 dark:hover:bg-white/5"
                } ${
                  isWan25
                    ? "shadow-[0_0_20px_rgba(251,191,36,0.3)] border-l-2 border-l-yellow-500/50"
                    : ""
                } ${
                  index !== models.length - 1
                    ? "border-b border-white/10 dark:border-white/10"
                    : ""
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isSelected ? "bg-white/20" : "bg-white/10"
                  }`}
                >
                  {model.logo_url ? (
                    <img
                      src={model.logo_url}
                      alt={model.public_name}
                      className="w-6 h-6 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        (e.currentTarget
                          .nextElementSibling as HTMLElement)!.style.display =
                          "block";
                      }}
                    />
                  ) : null}
                  <span
                    className="text-2xl"
                    style={{ display: model.logo_url ? "none" : "block" }}
                  >
                    {getModelIcon(model.name)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-base text-white dark:text-white">
                        {model.public_name}
                      </h4>
                      {isWan25 && (
                        <>
                          <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                            AUDIO
                          </span>
                          <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                            NEW
                          </span>
                        </>
                      )}
                    </div>
                    {!isFreePlan && (
                      <span className="bg-white/20 dark:bg-white/20 text-white text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap">
                        {model.credits} credits
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-300 dark:text-gray-300 leading-relaxed">
                    {model.description}
                  </p>
                </div>
                {isSelected && (
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-black"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ModelSelector;
